import { useEffect, useMemo, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { MessageCircle, Map, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const fmt = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

export default function PriceTable({ t, lang, agent }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [block, setBlock] = useState("all");
  const [type, setType] = useState("all");
  const [availability, setAvailability] = useState("available");
  const [showMap, setShowMap] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const toggleSort = (key) =>
    setSort((s) =>
      s.key !== key
        ? { key, dir: "asc" }
        : s.dir === "asc"
        ? { key, dir: "desc" }
        : { key: null, dir: "asc" },
    );

  useEffect(() => {
    if (!showMap) return;
    const onKey = (e) => e.key === "Escape" && setShowMap(false);
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showMap]);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL}/data/units.json`, { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => active && setData(d))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  const units = data?.units ?? [];
  const blocks = data?.blocks ?? [...new Set(units.map((u) => u.block))];
  const types = data?.types ?? [...new Set(units.map((u) => u.type))];

  const rows = useMemo(() => {
    const filtered = units.filter(
      (u) =>
        (block === "all" || u.block === block) &&
        (type === "all" || u.type === type) &&
        (availability === "all" ||
          (availability === "available" ? u.available : !u.available)),
    );
    if (!sort.key) return filtered;
    const k = sort.key;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[k] == null ? null : Number(a[k]);
      const bv = b[k] == null ? null : Number(b[k]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * factor;
    });
  }, [units, block, type, availability, sort]);

  const inquire = (u) => {
    const first = agent.name.trim().split(/\s+/)[0];
    const txt =
      lang === "pt"
        ? `Olá ${first}, tenho interesse na fração ${u.unit} (Bloco ${u.block}, ${u.type}) do Belo Horizonte Residences.`
        : `Hi ${first}, I'm interested in unit ${u.unit} (Block ${u.block}, ${u.type}) at Belo Horizonte Residences.`;
    window.open(
      `https://wa.me/${agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(txt)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <section
      id="prices"
      data-testid="prices-section"
      className="relative bg-[#F5F1EA] py-24 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-10 bg-[#BEAF87]" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-[#BEAF87] font-[Barlow_Semi_Condensed] font-medium">
                {t.prices.eyebrow}
              </span>
            </div>
            <h2
              data-testid="prices-title"
              className="font-[Cormorant_Garamond] font-light text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              {t.prices.title}
            </h2>
            <p className="mt-6 text-[#3a3a3a] font-[Barlow_Semi_Condensed] max-w-xl">
              {t.prices.subtitle}
            </p>
          </div>

          <div className="flex flex-wrap gap-4 lg:shrink-0">
            <Filter
              label={t.prices.filters.block}
              value={block}
              onChange={setBlock}
              testid="price-filter-block"
            >
              <SelectItem value="all">{t.prices.filters.all}</SelectItem>
              {blocks.map((b) => (
                <SelectItem key={b} value={b}>
                  {t.prices.filters.block} {b}
                </SelectItem>
              ))}
            </Filter>

            <div className="flex flex-col justify-end">
              <button
                type="button"
                onClick={() => setShowMap(true)}
                data-testid="price-block-map-trigger"
                className="inline-flex h-11 items-center gap-2 px-4 border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] hover:border-[#BEAF87] hover:text-[#BEAF87] transition-colors text-[12px] uppercase tracking-[0.15em] font-[Barlow_Semi_Condensed]"
              >
                <Map size={15} />
                {t.prices.filters.map}
              </button>
            </div>

            <Filter
              label={t.prices.filters.type}
              value={type}
              onChange={setType}
              testid="price-filter-type"
            >
              <SelectItem value="all">{t.prices.filters.all}</SelectItem>
              {types.map((ty) => (
                <SelectItem key={ty} value={ty}>
                  {ty}
                </SelectItem>
              ))}
            </Filter>

            <Filter
              label={t.prices.filters.availability}
              value={availability}
              onChange={setAvailability}
              testid="price-filter-availability"
            >
              <SelectItem value="all">{t.prices.filters.all}</SelectItem>
              <SelectItem value="available">
                {t.prices.filters.available}
              </SelectItem>
              <SelectItem value="unavailable">
                {t.prices.filters.unavailable}
              </SelectItem>
            </Filter>
          </div>
        </div>

        <div
          data-testid="price-table-container"
          className="bg-white border border-[#0A0A0A]/10 overflow-hidden"
        >
          {error ? (
            <div className="px-6 py-16 text-center text-[#0A0A0A]/60 font-[Barlow_Semi_Condensed]">
              {lang === "pt"
                ? "Não foi possível carregar a tabela de frações."
                : "Could not load the unit list."}
            </div>
          ) : !data ? (
            <div className="px-6 py-16 text-center text-[#0A0A0A]/50 font-[Barlow_Semi_Condensed]">
              {lang === "pt" ? "A carregar…" : "Loading…"}
            </div>
          ) : (
            <div className="table-scroll overflow-x-auto max-h-[640px] overflow-y-auto">
              <table className="w-full text-left text-sm font-[Barlow_Semi_Condensed]">
                <thead className="sticky top-0 bg-[#0A0A0A] text-white z-10">
                  <tr>
                    <Th>{t.prices.columns.block}</Th>
                    <Th>{t.prices.columns.unit}</Th>
                    <Th>{t.prices.columns.floor}</Th>
                    <Th>{t.prices.columns.type}</Th>
                    <Th sortKey="area" sort={sort} onSort={toggleSort}>
                      {t.prices.columns.area}
                    </Th>
                    <Th sortKey="balcony" sort={sort} onSort={toggleSort}>
                      {t.prices.columns.balcony}
                    </Th>
                    <Th sortKey="total" sort={sort} onSort={toggleSort}>
                      {t.prices.columns.total}
                    </Th>
                    <Th sortKey="launch" sort={sort} onSort={toggleSort}>
                      {t.prices.columns.launch}
                    </Th>
                    <Th className="text-right">{t.prices.columns.market}</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u, i) => {
                    const available = u.available;
                    const strike = available ? "" : "line-through";
                    return (
                      <tr
                        key={`${u.block}-${u.unit}`}
                        data-testid={`price-row-${u.block}-${u.unit}`}
                        data-available={available}
                        className={`${i % 2 === 0 ? "bg-white" : "bg-[#F5F1EA]/50"} border-t border-[#0A0A0A]/5 transition-colors ${
                          available
                            ? "hover:bg-[#BEAF87]/10"
                            : "text-[#0A0A0A]/40"
                        }`}
                      >
                        <Td className={strike}>{u.block}</Td>
                        <Td className={`font-medium ${strike}`}>{u.unit}</Td>
                        <Td className={strike}>{u.floor}</Td>
                        <Td>
                          <span
                            className={`inline-block px-2 py-0.5 text-xs tracking-wider ${
                              available
                                ? "bg-[#0A0A0A] text-white"
                                : "bg-[#0A0A0A]/30 text-white/70"
                            }`}
                          >
                            {u.type}
                          </span>
                        </Td>
                        <Td className={`text-right ${strike}`}>
                          {u.area ? `${u.area} m²` : "—"}
                        </Td>
                        <Td className={`text-right text-[#0A0A0A]/60 ${strike}`}>
                          {u.balcony != null ? `${u.balcony} m²` : "—"}
                        </Td>
                        <Td className={`text-right ${strike}`}>
                          {u.total != null ? `${u.total} m²` : "—"}
                        </Td>
                        <Td className="text-right font-semibold text-[#0A0A0A]">
                          {available ? fmt(u.launch) : "—"}
                        </Td>
                        <Td className="text-right text-[#0A0A0A]/50 line-through">
                          {available ? fmt(u.market) : "—"}
                        </Td>
                        <Td>
                          {available ? (
                            <button
                              data-testid={`price-inquire-${u.block}-${u.unit}`}
                              onClick={() => inquire(u)}
                              className="inline-flex items-center gap-1.5 text-xs text-[#0A0A0A] hover:text-[#BEAF87] uppercase tracking-widest"
                            >
                              <MessageCircle size={14} />
                              {t.prices.consult}
                            </button>
                          ) : (
                            <span
                              data-testid={`price-reserved-${u.block}-${u.unit}`}
                              className="inline-flex items-center gap-2 text-xs text-[#0A0A0A]/60 uppercase tracking-widest"
                            >
                              <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-600 shadow-[0_0_6px_rgba(220,38,38,0.7)]" />
                              {lang === "pt" ? "Reservado" : "Reserved"}
                            </span>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-[#0A0A0A]/50 font-[Barlow_Semi_Condensed]">
          {t.prices.disclaimer} · {rows.length}{" "}
          {lang === "pt" ? "frações" : "units"}
        </p>
      </div>

      {showMap && (
        <div
          data-testid="price-block-map-modal"
          role="dialog"
          aria-modal="true"
          aria-label={t.prices.filters.mapTitle}
          onClick={() => setShowMap(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]/95 backdrop-blur-sm p-6 animate-fade-up"
        >
          <button
            type="button"
            onClick={() => setShowMap(false)}
            data-testid="price-block-map-close"
            aria-label="Fechar"
            className="absolute top-5 right-5 lg:top-8 lg:right-8 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white hover:border-[#BEAF87] hover:text-[#BEAF87] transition-colors"
          >
            <X size={20} />
          </button>
          <figure
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col items-center max-w-[95vw] max-h-[90vh]"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/site-plan.webp`}
              alt={t.prices.filters.mapTitle}
              className="max-h-[82vh] max-w-full object-contain bg-white shadow-2xl"
            />
            <figcaption className="mt-4 text-center text-sm text-white/70 font-[Barlow_Semi_Condensed]">
              {t.prices.filters.mapTitle}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

function Filter({ label, value, onChange, testid, children }) {
  return (
    <div className="flex flex-col">
      <label
        htmlFor={testid}
        className="text-[11px] uppercase tracking-[0.25em] text-[#0A0A0A]/60 font-[Barlow_Semi_Condensed] font-medium mb-2"
      >
        {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={testid}
          data-testid={testid}
          className="w-[150px] bg-white border-[#0A0A0A]/20 rounded-none h-11 font-[Barlow_Semi_Condensed] text-[#0A0A0A]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}

function Th({ children, className = "", sortKey, sort, onSort }) {
  if (!sortKey) {
    return (
      <th
        className={`px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-medium ${className}`}
      >
        {children}
      </th>
    );
  }
  const active = sort?.key === sortKey;
  const Icon = !active
    ? ChevronsUpDown
    : sort.dir === "asc"
    ? ChevronUp
    : ChevronDown;
  return (
    <th
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className="px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-medium text-right"
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`price-sort-${sortKey}`}
        className={`inline-flex items-center gap-1 ml-auto uppercase tracking-[0.2em] transition-colors ${
          active ? "text-[#BEAF87]" : "text-white hover:text-[#BEAF87]"
        }`}
      >
        {children}
        <Icon size={13} className={active ? "opacity-100" : "opacity-50"} />
      </button>
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-[#0A0A0A] ${className}`}>{children}</td>;
}
